import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Slack, Save, Stethoscope, ChevronDown, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../utils/api';
import DataRepairCard from '../components/settings/DataRepairCard';
import DietTrialRepairCard from '../components/settings/DietTrialRepairCard';
import RefundCascadeRepairCard from '../components/settings/RefundCascadeRepairCard';
import RoutingPreviewCard from '../components/settings/RoutingPreviewCard';
import LmsTestDataCard from '../components/settings/LmsTestDataCard';
import OrphanPaymentsCard from '../components/settings/OrphanPaymentsCard';
import FxRatesCard from '../components/settings/FxRatesCard';

const API_URL = getApiUrl();

// Collapsible Section Component
const CollapsibleSection = ({ title, subtitle, icon: Icon, iconBg, iconColor, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full card-header flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-slate-900">{title}</h3>
            <p className="text-sm text-slate-500">{subtitle}</p>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`transition-all duration-200 ease-in-out ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
        <div className="card-body space-y-4 border-t border-slate-100">
          {children}
        </div>
      </div>
    </div>
  );
};

const SettingsPage = () => {
  const [settings, setSettings] = useState({
    slack_webhook_url: '',
    slack_reminders_webhook_url: '',
    slack_enabled: false,
    doctor_referral_bonus: 500,
    doctor_referral_enabled: true,
    mobile_app_ios_url: '',
    mobile_app_android_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAppUrls, setSavingAppUrls] = useState(false);
  const [copied, setCopied] = useState(null);
  const [preSalesUsers, setPreSalesUsers] = useState([]);

  useEffect(() => {
    fetchSettings();
    fetchUsers();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`, { withCredentials: true });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      const presales = response.data.filter(u => 
        ['super_admin', 'pre_sales'].includes(u.role) && u.status === 'active'
      );
      setPreSalesUsers(presales);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Backend expects POST with JSON body matching the Settings Pydantic model
      const body = {
        settings_id: 'app_settings',
        slack_webhook_url: settings.slack_webhook_url || null,
        slack_reminders_webhook_url: settings.slack_reminders_webhook_url || null,
        slack_enabled: !!settings.slack_enabled,
        doctor_referral_bonus: Number(settings.doctor_referral_bonus) || 0,
        doctor_referral_enabled: settings.doctor_referral_enabled !== false,
      };
      await axios.post(`${API_URL}/api/settings`, body, { withCredentials: true });
      toast.success('Settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAppUrls = async () => {
    setSavingAppUrls(true);
    try {
      const params = new URLSearchParams();
      params.append('ios_url', settings.mobile_app_ios_url || '');
      params.append('android_url', settings.mobile_app_android_url || '');
      await axios.post(
        `${API_URL}/api/settings/mobile-app-urls?${params.toString()}`,
        {},
        { withCredentials: true }
      );
      toast.success('App store URLs saved — will appear in next post-payment email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save app URLs');
    } finally {
      setSavingAppUrls(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(field);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-4 animate-fade-in max-w-2xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
          <p className="text-slate-500">Configure application integrations</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary flex items-center gap-2"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Slack Integration */}
      <CollapsibleSection
        title="Slack Notifications"
        subtitle="Get notified for payments and refunds"
        icon={Slack}
        iconBg="bg-purple-100"
        iconColor="text-purple-600"
        defaultOpen={false}
      >
        <div className="flex items-center justify-between">
          <div>
            <label className="font-medium text-slate-700">Enable Slack Notifications</label>
            <p className="text-sm text-slate-500">Send notifications to your Slack channel</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
                type="checkbox"
                checked={settings.slack_enabled}
                onChange={(e) => setSettings({ ...settings, slack_enabled: e.target.checked })}
                className="sr-only peer"
                data-testid="slack-toggle"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
            </label>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Webhook URL (Main Notifications)</label>
            <input
              type="url"
              value={settings.slack_webhook_url || ''}
              onChange={(e) => setSettings({ ...settings, slack_webhook_url: e.target.value })}
              placeholder="https://hooks.slack.com/services/..."
              className="input-base"
              data-testid="slack-webhook-url"
            />
            <p className="text-xs text-slate-500 mt-1">
              For enrollment notifications, payment confirmations, etc.
            </p>
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Reminders Webhook URL (Due Date Alerts)</label>
            <input
              type="url"
              value={settings.slack_reminders_webhook_url || ''}
              onChange={(e) => setSettings({ ...settings, slack_reminders_webhook_url: e.target.value })}
              placeholder="https://hooks.slack.com/services/..."
              className="input-base"
              data-testid="slack-reminders-webhook-url"
            />
            <p className="text-xs text-slate-500 mt-1">
              Separate channel for tenure expiry reminders (5 days before due date). Leave empty to use main webhook.
            </p>
          </div>
      </CollapsibleSection>

      {/* Doctor Referral Settings */}
      <CollapsibleSection
        title="Doctor-to-Doctor Referral"
        subtitle="Fixed bonus when a doctor refers another doctor"
        icon={Stethoscope}
        iconBg="bg-teal-100"
        iconColor="text-teal-600"
        defaultOpen={false}
      >
        <div className="flex items-center justify-between">
          <div>
            <label className="font-medium text-slate-700">Enable Doctor Referral Program</label>
            <p className="text-sm text-slate-500">Allow doctors to refer other doctors and earn bonus</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.doctor_referral_enabled}
              onChange={(e) => setSettings({ ...settings, doctor_referral_enabled: e.target.checked })}
              className="sr-only peer"
              data-testid="doctor-referral-toggle"
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
          </label>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Referral Bonus Amount (₹)</label>
          <div className="relative max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
            <input
              type="number"
              min="0"
              step="100"
              value={settings.doctor_referral_bonus || 500}
              onChange={(e) => setSettings({ ...settings, doctor_referral_bonus: parseFloat(e.target.value) || 0 })}
              className="input-base pl-8"
              placeholder="500"
              data-testid="doctor-referral-bonus"
            />
          </div>
          <p className="text-xs text-slate-500 mt-1">
            This fixed amount will be credited when a doctor successfully refers another doctor who gets onboarded
          </p>
        </div>
      </CollapsibleSection>

      {/* Mobile App Store URLs */}
      <CollapsibleSection
        title="Mobile App Install Links"
        subtitle="Shown in post-payment emails to drive app installs (empty = hidden)"
        icon={Smartphone}
        iconBg="bg-indigo-100"
        iconColor="text-indigo-600"
        defaultOpen={false}
      >
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">iOS App Store URL</label>
          <input
            type="url"
            value={settings.mobile_app_ios_url || ''}
            onChange={(e) => setSettings({ ...settings, mobile_app_ios_url: e.target.value })}
            placeholder="https://apps.apple.com/app/id..."
            className="input-base"
            data-testid="mobile-app-ios-url"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Google Play Store URL</label>
          <input
            type="url"
            value={settings.mobile_app_android_url || ''}
            onChange={(e) => setSettings({ ...settings, mobile_app_android_url: e.target.value })}
            placeholder="https://play.google.com/store/apps/details?id=..."
            className="input-base"
            data-testid="mobile-app-android-url"
          />
          <p className="text-xs text-slate-500 mt-1">
            Both URLs are optional. Set one or both — the email block auto-hides when both are empty.
            Changes apply to the next email sent (trial-class invoice + Elite welcome).
          </p>
        </div>
        <button
          onClick={handleSaveAppUrls}
          disabled={savingAppUrls}
          className="btn-primary flex items-center gap-2"
          data-testid="save-mobile-app-urls"
        >
          <Save className="w-4 h-4" />
          {savingAppUrls ? 'Saving...' : 'Save App URLs'}
        </button>
      </CollapsibleSection>

      {/* V42.7 — Configurable FX rates for the Sales Dashboard blended total */}
      <FxRatesCard />

      {/* V33.1 + V36 — Data Repair (per-record backfill + daily integrity audit + country heal) */}
      <RoutingPreviewCard />
      <DataRepairCard />
      <DietTrialRepairCard />
      <RefundCascadeRepairCard />

      {/* V19 — LMS V18 go-live: test coupons + learners, slab normalization */}
      <LmsTestDataCard />

      {/* V20 — attach orphan payments to the right lead */}
      <OrphanPaymentsCard />

      {/* V25 — Secret Rotation Auto-Cleanup status */}
      <SecretRotationCard />
    </div>
  );
};

export default SettingsPage;

// ─── V15 + V17 Pricing Migration Card removed Feb 2026 ─────────────────────
// One-time migration already applied long ago; status banner permanently
// showed "Already applied" — UI noise with no actionable use case.

// ─── V25 — Secret Rotation Auto-Cleanup status card ───────────────────────
// Shows current rotation lifecycle, safety gate status, manual override.

const SecretRotationCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/settings/secret-rotation/status`, {
        withCredentials: true, timeout: 30000,
      });
      setStatus(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load rotation status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleRunCleanup = async (force = false) => {
    if (!window.confirm(
      force
        ? 'Force-run cleanup, BYPASSING all safety gates?\nOnly do this if you are 100% sure LMS has cut over to NEW secrets.'
        : 'Run Phase 3 cleanup now?\nThis will:\n1. Remove OPS_*_OLD slots from .env\n2. Restart backend\n3. Smoke-test (auto-rollback on failure)\n\nContinue?',
    )) return;
    setRunning(true);
    try {
      toast.info('Cleanup running… (backend will restart; this page may briefly disconnect)');
      await axios.post(
        `${API_URL}/api/settings/secret-rotation/run-cleanup`,
        {},
        { withCredentials: true, params: { force }, timeout: 60000 },
      );
      await new Promise(r => setTimeout(r, 6000));
      await fetchStatus();
      toast.success('Cleanup complete. Status refreshed.');
    } catch (err) {
      // After backend restart the request may drop — wait + reload
      try {
        await new Promise(r => setTimeout(r, 6000));
        await fetchStatus();
        toast.success('Cleanup likely succeeded (request dropped during expected restart).');
      } catch {
        toast.error(err.response?.data?.detail || 'Cleanup failed; check logs.');
      }
    } finally {
      setRunning(false);
    }
  };

  const gates = status?.safety_gates || {};
  const cleanupPending = status?.cleanup_pending;
  const allGatesPass = gates.all_pass;

  return (
    <div className="card mt-6" data-testid="secret-rotation-card">
      <div className="card-body">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Secret Rotation — Auto-Cleanup</h3>
            <p className="text-sm text-slate-500 mt-1">
              Phase 3 cleanup runs automatically every 24 h once safety gates pass.
              You can also force-run from here.
            </p>
          </div>
          <button
            onClick={fetchStatus}
            disabled={loading}
            className="text-xs text-indigo-600 hover:text-indigo-700 whitespace-nowrap"
            data-testid="secret-rotation-refresh-btn"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {!status && !loading && (
          <p className="text-sm text-slate-500">No data yet — click Refresh.</p>
        )}

        {status && (
          <>
            {!cleanupPending && (
              <div className="mb-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm" data-testid="secret-rotation-clean-banner">
                <span className="font-medium text-emerald-900">No cleanup pending.</span>
                <span className="text-emerald-700 ml-1">No legacy slots in .env.</span>
                {status.last_phase3_cleanup_at && (
                  <div className="text-xs text-emerald-600 mt-1">
                    Last cleanup: {new Date(status.last_phase3_cleanup_at).toLocaleString()}
                  </div>
                )}
              </div>
            )}

            {cleanupPending && (
              <div className={`mb-3 p-3 rounded-lg border text-sm ${allGatesPass ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`} data-testid="secret-rotation-pending-banner">
                <div className="font-medium text-slate-900 mb-2">
                  Cleanup pending: <code className="bg-white px-1 py-0.5 rounded text-xs">{status.legacy_slots_present.join(', ')}</code>
                </div>
                {status.phase2_cutover_at && (
                  <div className="text-xs text-slate-600 mb-2">
                    Phase 2 cutover: {new Date(status.phase2_cutover_at).toLocaleString()}
                  </div>
                )}
                <div className="space-y-1 mt-2">
                  {Object.entries(gates).filter(([k]) => k !== 'all_pass').map(([name, info]) => (
                    <div key={name} className="flex items-start gap-2 text-xs" data-testid={`secret-rotation-gate-${name}`}>
                      <span className={info.pass ? 'text-emerald-600' : 'text-amber-600'}>
                        {info.pass ? '✓' : '○'}
                      </span>
                      <span className="text-slate-700">
                        <span className="font-medium">{name}:</span> {info.reason}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {status.next_rotation_due_at && (
              <div className={`mb-3 p-3 rounded-lg border text-sm ${status.rotation_due ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200'}`} data-testid="secret-rotation-next-due">
                {status.rotation_due ? (
                  <span className="font-medium text-rose-900">Next rotation overdue.</span>
                ) : (
                  <span className="font-medium text-slate-700">
                    Next rotation due in {status.days_until_next_rotation} days
                  </span>
                )}
                <span className="text-xs text-slate-500 ml-2">
                  ({new Date(status.next_rotation_due_at).toLocaleDateString()})
                </span>
              </div>
            )}

            {cleanupPending && (
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => handleRunCleanup(false)}
                  disabled={running || !allGatesPass}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-slate-400 disabled:cursor-not-allowed text-sm font-medium"
                  data-testid="secret-rotation-run-btn"
                >
                  {running ? 'Running…' : 'Run cleanup now'}
                </button>
                <button
                  onClick={() => handleRunCleanup(true)}
                  disabled={running}
                  className="px-4 py-2 bg-white text-rose-600 border border-rose-300 rounded-lg hover:bg-rose-50 disabled:opacity-50 text-sm font-medium"
                  data-testid="secret-rotation-force-btn"
                >
                  Force (skip gates)
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
